import { ITheme, Theme } from "@fluentui/react";
import type { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { saveAs } from "file-saver";

import {
  BUILD_NUMBER,
  BUILD_OR_JOB_NUMBER,
  BUILD_SOURCE_SYSTEM_REGEX,
  CAP_DC_TOPOLOGY_ROLE,
  CHECKOUT_NOT_SUPPORTED,
  CUSTOM,
  CUSTOM_NOT_SUPPORTED,
  DARK_THEME_NAME,
  DEFAULT_RDP_USERNAME,
  DOMAINS,
  EOP_TOPOLOGY_IDENTIFIER,
  MATCH_NON_WORD_CHARACTERS,
  OPTIONAL_REPLACEABLES,
  OSSINFRA_DROP_BASEURL,
  OSSINFRA_DROPEXPLORER_BASEURL,
  RDP_TEMPLATE,
  TOPOLOGY_TO_OS,
} from "../constants/constants";
import {
  ErrorMessageAction,
  IConnectionTypes,
  IMachineExt,
  ITopology,
  OS,
  Replaceable,
  ReplaceableType,
  TopologyType,
} from "../constants/types";
import { determineAlternativeReplaceableName } from "../redux/slices/newTopologySlice";
import { IEnvironment } from "./environment";
import { isAzureEnvironment } from "./Utils";

/**
 * Validates that a URI is a drop URL by checking if it begins with "https://ossinfra.artifacts.visualstudio.com"
 * @param dropUri url to validate
 */
export function validateDropURI(dropUri: string): boolean {
  return dropUri.startsWith(OSSINFRA_DROP_BASEURL);
}

/**
 * Generates a URL to view the contents of a Drop using the Drop Explorer.
 * @param {string} dropUrl drop to create browse URL for
 * @returns {string} Fully formatted URL
 */
export function generateDropExplorerUrl(dropUrl: string): string {
  const dropName = dropUrl.substring(dropUrl.indexOf("secondary"));

  return OSSINFRA_DROPEXPLORER_BASEURL + dropName;
}

/**
 * Given a template path/name return the formatted text in the necessary XML nodes
 * for the lookup files
 * @param templatePath the path of the template to wrap
 * @returns the XML expression for the TopobuilderTemplateRelativePath express
 */
export function generateTemplateRelativePathXml(templatePath: string): string {
  const openTag = "<TopobuilderTemplateRelativePath>";
  const closeTag = "</TopobuilderTemplateRelativePath>";

  return openTag + templatePath + closeTag;
}

/**
 * Creates an email template for reaching out to the owner of the topology
 * that also includes helpful links to the topology's templates, and CC's
 * the o365infratds@microsoft.com alias.
 *
 * @param topology the topology type
 * @param name the name of the customer emailing
 * @returns the formatted mailto link for the Topology owner
 */
export const generateTopologyOwnerMailto = (topology: TopologyType, name: string): string => {
  const { TemplateOwnerName: owner, environment, DisplayName: topologyDisplayName } = topology;
  const { environmentType } = environment;

  const to = `${owner}@microsoft.com`;
  const cc = "O365InfraTDS@microsoft.com";

  const environmentIndicator = `[${environmentType} ${environment.name}]`;
  const subject = encodeURI(`${environmentIndicator} Question about ${topologyDisplayName}`);

  const checkoutLink = `Link to Checkout Template: ${generateLookupFileLink(
    environment,
    topology.CheckOutLookupFileName,
  )}`;
  const customLink = `Link to Deployment (Blessed) Template: ${generateLookupFileLink(
    environment,
    topology.DeploymentLookupFileName,
  )}`;

  const body = `Hello ${owner},

I am in the process of checking out the "${topologyDisplayName}" topology in the ${environmentType} environment and need some assistance; you are listed as the topology owner on TDS Web.

<<<INSERT QUESTION HERE>>>

Thanks,

${name}

${checkoutTopologySupported(topology.CheckOutLookupFileName) ? checkoutLink : ""}
${customTopologySupported(topology.DeploymentLookupFileName) ? customLink : ""}
`;

  return `mailto:${to}?cc=${cc}&subject=${subject}&body=${encodeURIComponent(body)}`;
};

/**
 * Given an input string, validate that it is a proper microsoft.com email address. Returns undefined if no errors were found
 *
 * @param inputAlias input alias as a string
 * @returns an error message if the input alias is not valid
 */
export function validateMicrosoftAlias(inputAlias: string): string | undefined {
  const domainPattern = "(?:" + DOMAINS.join("\\.|").toLowerCase() + "\\.)";
  const microsoftRegex = new RegExp("^((a|b|t|v)[-])?[a-zA-Z0-9]+@" + domainPattern + "?microsoft\\.com$", "i");

  if (!microsoftRegex.test(inputAlias.toLowerCase())) {
    const possibleEmails = DOMAINS.map((d) => `@${d}.microsoft.com`)
      .join(", ")
      .toLowerCase();

    return `Unrecognized Microsoft alias. Valid email formats: 'alias@microsoft.com', 't-alias@microsoft.com', or standard email format with a valid domain specifier: ${possibleEmails}`;
  }

  return undefined;
}

export function validateTopologyName(topologyName: string): string {
  if (topologyName === undefined || topologyName === null) {
    return "Topology Name is either null or undefined, this is likely an error on our part. Please reach out to danigon@microsoft.com if you see this error.";
  }

  if (topologyName.length < 3 || topologyName.length > 100) {
    return "Invalid length: Topology Name must be between 3 and 100 characters long.";
  }

  if (topologyName.includes("#")) {
    return "Invalid character: Topology Name cannot include the # symbol.";
  }

  return "";
}

/**
 * Determines if the theme mode is dark theme
 * @param theme the theme
 * @returns true if the theme mode is dark theme
 */
export const isDarkModeTheme = (theme: Theme) => theme.name === DARK_THEME_NAME;

/**
 * Gets the type of connection to be made
 * @param environment the environemnt type
 * @param usingTdsVpn whether to use TDS VPN
 * @returns the connection type string: "TDSVPN", "CORPNET", or "RDP"
 */
export const getConnectionType = (environment: IEnvironment, usingTdsVpn: boolean): IConnectionTypes => {
  const isCloudTopology = environment.environmentType === "Cloud";

  if (isCloudTopology) {
    if (usingTdsVpn) return "TDSVPN";
    return "CORPNET";
  }

  return "RDP";
};

/**
 * Gets the RDP file content
 * @param machine the machine to be used
 * @param username the username to connect with (defaults to the local administrator account)
 * @returns the RDP file content
 */
export const getRdpFileContent = (machine: string, username: string = DEFAULT_RDP_USERNAME) =>
  RDP_TEMPLATE.replace("{0}", machine).replace("{1}", username);

/**
 * Gets the RDP username to connect with for a topology's machines. For EOP topologies, the
 * forest admin account on the CAP-DC machine is used, in the form "<CAP-DC name>D\Administrator".
 * If the topology is not an EOP topology, or the CAP-DC machine has not been provisioned yet,
 * the default local administrator account is returned.
 *
 * @param topology the topology the machine belongs to
 * @returns the RDP username to use
 */
export const getForestAdminUsername = (topology: ITopology): string => {
  if (!topology.TopologyType.DisplayName.includes(EOP_TOPOLOGY_IDENTIFIER)) {
    return DEFAULT_RDP_USERNAME;
  }

  const capDcMachine = topology.MachinesExt.find((machine) => machine.TopologyRole === CAP_DC_TOPOLOGY_ROLE);

  return capDcMachine ? `${capDcMachine.Name}D\\Administrator` : DEFAULT_RDP_USERNAME;
};

/**
 * Saves an RDP file for the user. If the environment is an azure environment and
 * they are not opting to use TDSVPN, it will use the FQDN in the RDP file. Otherwise,
 * it will use the machine's IP Address.
 *
 * @param machine the machine to create the RDP connect file for
 * @param environment the environment for the machine
 * @param useTdsVpn whether or not to use TDSVPN
 * @param username the username to connect with (defaults to the local administrator account)
 */
export const saveRdpFileForMachine = (
  machine: IMachineExt,
  environment: IEnvironment,
  useTdsVpn: boolean,
  username: string = DEFAULT_RDP_USERNAME,
) => {
  const rdpFileContent =
    isAzureEnvironment(environment) && !useTdsVpn
      ? getRdpFileContent(machine.Fqdn + ":" + machine.RdpPort, username)
      : getRdpFileContent(machine.IPAddress, username);

  // Note: Ie and Edge don't support the new File constructor,
  // so it's better to construct blobs and use saveAs(blob, filename)
  const blob = new Blob([rdpFileContent], { type: "application/rdp; charset=utf-8" });

  saveAs(blob, machine.Name + ".rdp");
};

/**
 * Generates a link to the Topologies Repo to view the contents of the Lookup File.
 *
 * @param environment the environment for the Topology
 * @param lookupFileName the name of the lookup file
 * @returns the URL to view the contents of the lookup file in the Topologies repo
 */
export const generateLookupFileLink = (environment: IEnvironment, lookupFileName: string): string => {
  const environmentPath = environment.environmentType === "Cloud" ? "EFDC" : "OnPrem";

  return `https://o365exchange.visualstudio.com/O365%20Core/_git/Topologies?path=/sources/dev/TopologyLookupFiles/EDS/${environmentPath}/Official/${lookupFileName}.xml&_a=contents&version=GBmaster`;
};

/**
 * Finds the repo name from the topology display name.
 * Example: (Utah) Basic Domain @ Windows 2012 R2 --> Utah
 *
 * @param topologyDisplayName the topology display name
 * @returns the repo name
 */
export const findTopologyRepoName = (topologyDisplayName: string) => {
  const regExp = /^\(([^)]+)\)/;
  return regExp.exec(topologyDisplayName);
};

/**
 * Parses the repo name from the topology display name.
 * Example: (Utah) Basic Domain @ Windows 2012 R2 --> Utah
 *
 * @param topologyDisplayName the topology display name
 * @returns the repo name
 */
export const parseTopologyRepoName = (topologyDisplayName: string): string => {
  const matches = findTopologyRepoName(topologyDisplayName);
  if (!matches) {
    if (topologyDisplayName.includes("Infrastructure")) {
      return "Bothell";
    } else if (topologyDisplayName === "TDF (CustomBuildOnly)") {
      return "Griffin";
    }
    return "N/A";
  }
  return matches[1];
};

/**
 * Cleans repo name from the topology display name.
 * Example: (Utah) Basic Domain @ Windows 2012 R --> Basic Domain @ Windows 2012 R2
 *
 * @param topologyDisplayName the topology display name
 * @returns cleaned topology name
 */
export const cleanRepoNameFromTopologyName = (topologyDisplayName: string) => {
  const matches = findTopologyRepoName(topologyDisplayName);
  return matches ? topologyDisplayName.substring(matches[1].length + 2) : topologyDisplayName;
};

/**
 * Returns true or false if Custom Build Only was found in the topology display name.
 * Example: (Utah) Basic Domain @ Windows 2012 R2 (Custom Build Only) --> true
 *
 * @param topologyDisplayName the topology display name
 * @returns true or false if Custom Build Only was found
 */
export const isCustomBuildOnlyTopology = (topologyDisplayName: string) =>
  topologyDisplayName.includes("(Custom Build Only)") || topologyDisplayName.includes("(CustomBuildOnly)");

/**
 * Parses the os name from the topology display name.
 * Example: (Utah) Basic Domain @ Windows 2012 R2 --> Windows 2012 R2
 *
 * @param topologyDisplayName the topology display name
 * @returns the os name
 */
export const parseOSFromTopologyName = (topologyDisplayName: string): OS | undefined => {
  const os = TOPOLOGY_TO_OS.get(topologyDisplayName);
  if (os) {
    return os;
  } else if (topologyDisplayName.includes("Windows 2012")) {
    return "Windows 2012R2";
  } else if (topologyDisplayName.includes("Windows 2016") || topologyDisplayName.includes("Windows Threshold")) {
    return "Windows Threshold";
  } else if (topologyDisplayName.includes("Windows 2019")) {
    return "Windows 2019 Core";
  } else if (topologyDisplayName.includes("Evergreen")) {
    return "Windows Evergreen";
  }
  return undefined;
};

export const sortAndRemoveDuplicates = (topologies: TopologyType[]) => {
  // Sort by Display Name and Topology Type
  const sorted = topologies.sort((first, second) => {
    const firstType = first.environment.environmentType;
    const secondType = second.environment.environmentType;
    const firstName = first.DisplayName.replace(MATCH_NON_WORD_CHARACTERS, "");
    const secondName = second.DisplayName.replace(MATCH_NON_WORD_CHARACTERS, "");

    // If they're the same environment type, sort by alphabet
    if (firstType === secondType) {
      return firstName < secondName ? -1 : 1;
    }

    // We want to show Cloud topologies before Classic
    if (firstType === "Cloud" && secondType === "Classic") {
      return -1;
    } else {
      return 1;
    }
  });

  const cloudTopologyDisplayNames = sorted
    .filter((t) => t.environment.environmentType === "Cloud")
    .map((t) => t.DisplayName);

  // Removes duplicate classic topologies to give preference to cloud topologies
  const duplicatesRemoved = sorted.filter((t) => {
    return !(t.environment.environmentType === "Classic" && cloudTopologyDisplayNames.indexOf(t.DisplayName) >= 0);
  });

  return duplicatesRemoved;
};

export const filterTopologies = (topologies: TopologyType[], filterText: string): TopologyType[] => {
  if (filterText === "") return topologies;

  const queryParts = filterText.toLowerCase().split(MATCH_NON_WORD_CHARACTERS);

  return topologies.filter((t) => {
    const strippedName = t.DisplayName.replace(MATCH_NON_WORD_CHARACTERS, "").toLowerCase();
    return queryParts.every((part) => strippedName.includes(part));
  });
};

/**
 * A quick helper function to create a border style for dark mode view
 * so that elements like callouts and Topology Cards have more contrast
 *
 * @param theme the current theme
 * @returns A partial style for a border to use in dark mode
 */
export const applyDarkModeBorderStyles = (theme: ITheme) =>
  isDarkModeTheme(theme) && {
    border: `1px solid ${theme.palette.neutralLight}`,
  };

export const generateFeedbackUrl = (username: string): string => {
  const to = "O365InfraTDS@microsoft.com";
  const subject = "TDS%20Web%20Feedback";

  return `mailto:${to}?subject=${subject}&body=Hello%20TDS%20Web%20admins%2C%0A%0A%3CInsert%20your%20feedback%20here%2C%20good%20or%20bad%3B%20we%20appreciate%20both!%20%E2%9D%A4%EF%B8%8F%3E%0A%0A%0AThanks%2C%0A${username}`;
};

/**
 * Creates the right action type and converts an Error to a string if needed.
 *
 * @param type the action type
 * @param error the error
 * @returns the action type and error message
 */
export const createErrorMessageAction = (type: string, error: string | Error): ErrorMessageAction => {
  let errorMessage: string;

  if (error instanceof Error) {
    errorMessage = error.message;
  } else {
    errorMessage = error;
  }

  return {
    type,
    error: errorMessage,
  };
};

/**
 * Indicates whether a topology supports customization
 *
 * @param deploymentLookupFileName The deployment lookup filename
 * @returns True if the topology support customization
 */
export const customTopologySupported = (deploymentLookupFileName: string) => {
  return !CUSTOM_NOT_SUPPORTED.includes(deploymentLookupFileName);
};

export const checkoutTopologySupported = (checkoutLookupFileName: string) => {
  return !CHECKOUT_NOT_SUPPORTED.includes(checkoutLookupFileName);
};

/**
 * Type guard for checking whether an RTK Query Error is a {@link FetchBaseQueryError},
 * and if it's not, it's a {@link SerializedError}.
 *
 * This function is an example of a Type Guard. To learn more:
 *  - https://www.typescriptlang.org/docs/handbook/2/narrowing.html#the-in-operator-narrowing
 *  - https://www.typescriptlang.org/docs/handbook/2/narrowing.html#using-type-predicates
 *
 * @param error the RTK Query error
 * @returns whether the provided error is a {@link FetchBaseQueryError}
 */
export const isFetchBaseQueryError = (error: FetchBaseQueryError | SerializedError): error is FetchBaseQueryError => {
  return "status" in error;
};

/**
 * Simple helper function to make creating Default Topology Name's based on the Selected Build consistent
 *
 * @param type Topology Type
 * @param suffix The suffix to use in name
 * @returns Formatted default topology name with suffix
 */
export const createDefaultTopologyName = (type: TopologyType, suffix: string): string => {
  // Don't show <<CUSTOM>> in the Topology Name
  if (suffix === CUSTOM) return type.DisplayName;

  // If the suffix exists, and is not <<CUSTOM>>, append it to the Topology Name
  return suffix ? type.DisplayName + " " + suffix : type.DisplayName;
};

/**
 * Inserts spaces into the string between capital letters
 * @example
 * "BuildSourceSystem" --> "Build Source System"
 * @param {string} name The string to split
 * @returns {string} The string with spaces inserted in them
 */
export const splitName = (name: string) => {
  // For labels, split on _ or PascalCasing

  // Remove symbols first
  let tempString = name.replace(/[^a-zA-Z0-9]/g, "");

  // Split based on Caps
  tempString = tempString.split(/(?=[A-Z])/).join(" ");

  // Remove spaces between caps, if any exist
  const patt = /([A-Z])\s(?=[A-Z]\b)/g;

  if (patt.test(tempString)) {
    tempString = tempString.replace(patt, "$1");
  }

  return tempString;
};

/**
 * Inserts hyphen into the string between capital letters
 * and make all letters lowercase
 * @example
 * "BuildSourceSystem" --> "build-source-system"
 * "FocusJobID" --> "focus-job-id"
 * @param {string} name The string to split
 * @returns {string} The string with hyphens insterted
 */
export const hyphenateName = (name: string) => {
  // For labels, split on _ or PascalCasing

  // Remove symbols first
  let tempString = name.replace(/[^a-zA-Z0-9]/g, "");

  // Split based on caps
  tempString = tempString.split(/(?=[A-Z])/).join("-");

  // Remove hyphens between caps, if any exist
  const patt = /([A-Z])-(?=[A-Z]\b)/g;

  if (patt.test(tempString)) {
    tempString = tempString.replace(patt, "$1");
  }

  return tempString.toLowerCase();
};

/**
 * Determine the type of a Replaceable from it's name.
 *
 * @param replaceable the replaceable
 * @returns the determined {@link ReplaceableType}
 */
export const getReplaceableType = (replaceable: Replaceable): ReplaceableType => {
  if (isSourceSystem(replaceable.Name)) {
    return "BuildSourceSystemReplaceable";
  } else if (isBuildNumber(replaceable.Name)) {
    return "BuildNumberReplaceable";
  } else if (replaceable.Name.includes("Flavor")) {
    return "FlavorReplaceable";
  } else {
    return "StandardReplaceable";
  }
};

export const replaceableIsRequired = (replaceableName: string) => !OPTIONAL_REPLACEABLES.includes(replaceableName);

/**
 * Validates that all replaceables are defined
 * @param replaceables The list of replaceables
 * @param setError Sets the error
 * @returns Will set the error if all replaceables do not have a value
 */
export const validateAllReplaceablesAreDefined = (replaceables: Replaceable[]): string | null => {
  const copyReplaceables = [...replaceables];

  // Validate that all replaceables are defined
  // Filter for unspecified replaceables
  // skipping and replaceables listed in OPTIONAL_REPLACEABLES
  const unspecifiedReplaceables = copyReplaceables.filter(
    (r: Replaceable) =>
      replaceableIsRequired(r.Name) && (r.Value === null || r.Value === "" || r.Value.trim().length === 0),
  );

  // Check that all replaceables have a value
  if (unspecifiedReplaceables.length > 0) {
    const replaceableNames = unspecifiedReplaceables
      .map((r: Replaceable) => splitName(determineAlternativeReplaceableName(replaceables, r) ?? r.Name))
      .join(", ");

    return (
      "A value must be specified for all required replaceable fields and cannot be left blank. Replaceable fields without values specified are: " +
      replaceableNames
    );
  }

  return null;
};

export const generateWikiLink = (replaceable: string) => {
  return `https://o365exchange.visualstudio.com/O365%20Core/_wiki/wikis/O365%20Core.wiki/55661/TDS-Replaceables?anchor=${hyphenateName(
    replaceable,
  )}`;
};

export const isSourceSystem = (replaceableName: string): boolean => {
  return replaceableName.match(BUILD_SOURCE_SYSTEM_REGEX) ? true : false;
};

export const isBuildNumber = (replaceableName: string): boolean => {
  if (replaceableName.includes(BUILD_NUMBER) || replaceableName.includes(BUILD_OR_JOB_NUMBER)) {
    return true;
  }
  return false;
};
